import { useEffect, useState } from "react";
import { useAuth } from "../../auth/AuthContext";
import {
  getDocumentSettings,
  saveDocumentSettings,
  type DocumentBusinessIdentity,
  type DocumentSettings,
} from "../../lib/api";
import styles from "./DocumentSettingsPage.module.css";

/**
 * /admin/documents — the payment and remittance details printed on invoice
 * PDFs (DEC-CDD-009).
 *
 * Two halves, deliberately different in kind:
 * - Letterhead: read-only. Business name, ABN, address and logo are deployment
 *   configuration (env, DEC-CDD-005), shown here so an admin can see what the
 *   documents carry without reading a server file.
 * - Payment details: editable, saved to the database, live on the next PDF —
 *   no restart, no deploy.
 *
 * Every field is optional; a blank field is simply not printed.
 */

/** The editable fields, in the order they appear on the page and on the PDF. */
const FIELDS: Array<{
  key: keyof DocumentSettings;
  label: string;
  hint?: string;
  multiline?: boolean;
  wide?: boolean;
  placeholder?: string;
}> = [
  {
    key: "paymentTerms",
    label: "Payment terms",
    hint: "One line, printed at the top of the payment block.",
    placeholder: "Payment due within 14 days of the invoice date.",
    wide: true,
  },
  { key: "bankAccountName", label: "Account name", placeholder: "AFS Services Pty Ltd" },
  { key: "bankBsb", label: "BSB", placeholder: "000-000" },
  { key: "bankAccountNumber", label: "Account number", placeholder: "00000000" },
  {
    key: "paymentReference",
    label: "Payment reference",
    hint: "What the client should quote when they pay.",
    placeholder: "Please quote the invoice number.",
  },
  {
    key: "otherPaymentMethods",
    label: "Other payment methods",
    hint: "PayID, card payment link, cheque details — anything beyond the bank transfer above.",
    multiline: true,
    wide: true,
  },
  {
    key: "invoiceFooterNote",
    label: "Invoice footer note",
    hint: "Small print below the payment block: late-fee terms, disputes, anything standing.",
    multiline: true,
    wide: true,
  },
];

/** Blank record used before the first load resolves. */
const EMPTY: DocumentSettings = {
  paymentTerms: "",
  bankAccountName: "",
  bankBsb: "",
  bankAccountNumber: "",
  paymentReference: "",
  otherPaymentMethods: "",
  invoiceFooterNote: "",
  updatedBy: null,
  updatedAt: null,
};

function formatUpdated(settings: DocumentSettings): string | null {
  if (!settings.updatedAt) return null;
  const when = new Date(settings.updatedAt);
  const stamp = Number.isNaN(when.getTime()) ? settings.updatedAt : when.toLocaleString();
  return settings.updatedBy ? `Last saved by ${settings.updatedBy} on ${stamp}` : `Last saved ${stamp}`;
}

export function DocumentSettingsPage(): JSX.Element {
  const { canManageUsers } = useAuth();
  const [form, setForm] = useState<DocumentSettings>(EMPTY);
  const [saved, setSaved] = useState<DocumentSettings>(EMPTY);
  const [business, setBusiness] = useState<DocumentBusinessIdentity | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    if (!canManageUsers) return;
    let cancelled = false;
    getDocumentSettings()
      .then((data) => {
        if (cancelled) return;
        setForm(data.settings);
        setSaved(data.settings);
        setBusiness(data.business);
      })
      .catch((loadError: Error) => {
        if (!cancelled) setError(loadError.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [canManageUsers]);

  if (!canManageUsers) {
    return (
      <section className={styles.root}>
        <p className={styles.deny}>You do not have permission to manage document settings.</p>
      </section>
    );
  }

  // Compared against the last saved record so the Save button reflects whether
  // there is anything to save, not merely whether the form was touched.
  const dirty = FIELDS.some(({ key }) => form[key] !== saved[key]);

  const update = (key: keyof DocumentSettings, value: string): void => {
    setForm((current) => ({ ...current, [key]: value }));
    setNotice(null);
  };

  const handleSubmit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const result = await saveDocumentSettings(form);
      setForm(result.settings);
      setSaved(result.settings);
      setNotice("Saved. New invoice PDFs will carry these details.");
    } catch (saveError) {
      setError((saveError as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const updated = formatUpdated(saved);

  return (
    <section className={styles.root}>
      <div className={styles.head}>
        <div className={styles.headline}>
          <h1 className={styles.title}>Document settings</h1>
          <p className={styles.lede}>
            How clients are asked to pay. These details print on every invoice PDF — quotes are
            unaffected. Leave a field blank to keep it off the document.
          </p>
        </div>
      </div>

      {error && <p className={styles.error}>{error}</p>}
      {notice && <p className={styles.notice}>{notice}</p>}

      {business && (
        <div className={styles.identity}>
          <h2 className={styles.identityTitle}>Letterhead</h2>
          <p className={styles.identityHint}>
            Set in the server environment (<code>BUSINESS_*</code>), so changing these needs a
            deploy or restart rather than an edit here.
          </p>
          <dl className={styles.identityGrid}>
            <div className={styles.identityRow}>
              <dt className={styles.identityLabel}>Business name</dt>
              <dd className={styles.identityValue}>{business.name || "—"}</dd>
            </div>
            <div className={styles.identityRow}>
              <dt className={styles.identityLabel}>ABN</dt>
              <dd className={styles.identityValue}>{business.abn || "Not set"}</dd>
            </div>
            <div className={styles.identityRow}>
              <dt className={styles.identityLabel}>Address</dt>
              <dd className={styles.identityValue}>{business.address || "Not set"}</dd>
            </div>
            <div className={styles.identityRow}>
              <dt className={styles.identityLabel}>Email</dt>
              <dd className={styles.identityValue}>{business.email || "Not set"}</dd>
            </div>
            <div className={styles.identityRow}>
              <dt className={styles.identityLabel}>Phone</dt>
              <dd className={styles.identityValue}>{business.phone || "Not set"}</dd>
            </div>
            <div className={styles.identityRow}>
              <dt className={styles.identityLabel}>Logo</dt>
              <dd className={styles.identityValue}>
                {business.hasLogo ? "Configured" : "Not set — documents print the business name"}
              </dd>
            </div>
          </dl>
        </div>
      )}

      <form className={styles.form} onSubmit={handleSubmit}>
        <h2 className={styles.formTitle}>Payment details</h2>
        <div className={styles.grid}>
          {FIELDS.map((field) => (
            <div
              key={field.key}
              className={field.wide ? `${styles.field} ${styles.fieldWide}` : styles.field}
            >
              <label className={styles.label} htmlFor={`setting-${field.key}`}>
                {field.label}
              </label>
              {field.multiline ? (
                <textarea
                  id={`setting-${field.key}`}
                  className={styles.textarea}
                  rows={3}
                  value={String(form[field.key] ?? "")}
                  placeholder={field.placeholder}
                  disabled={loading || saving}
                  onChange={(event) => update(field.key, event.target.value)}
                />
              ) : (
                <input
                  id={`setting-${field.key}`}
                  className={styles.input}
                  type="text"
                  value={String(form[field.key] ?? "")}
                  placeholder={field.placeholder}
                  disabled={loading || saving}
                  onChange={(event) => update(field.key, event.target.value)}
                />
              )}
              {field.hint && <p className={styles.hint}>{field.hint}</p>}
            </div>
          ))}
        </div>

        <div className={styles.actions}>
          {updated && <span className={styles.stamp}>{updated}</span>}
          <button type="submit" className={styles.primaryButton} disabled={loading || saving || !dirty}>
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </form>
    </section>
  );
}
